import {
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import { callMutation } from "shared/api/callMutation"
import { evictProjectPrice } from "shared/api/price/evictPriceCache"
import { mapAPITypeToTypename } from "shared/api/serializer/mapApiTypeToTypename"
import { mapTypenameToAPIType } from "shared/api/serializer/mapTypenameToAPIType"
import { MutationSchema, MutationTypename } from "shared/api/types"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { Floor } from "shared/types/Floor"
import {
  FloorTypename,
  ProjectParentAPIType,
  ProjectParentTypename
} from "shared/types/Project"
import {
  CREATE_FLOOR,
  CREATE_PROJECT_CHILD
} from "shared/types/ProjectMutations"
import { CreateFloorInput } from "shared/types/__generated__/graphql"
import {
  CreateFloorInputSchema,
  FloorSchema
} from "shared/types/__generated__/validation"

import {
  CreateFloorDocument,
  CreateFloorMutation,
  CreateFloorMutationVariables
} from "entities/floors/graphql/mutations.generated"
import { getChildPositionData } from "entities/project-children/lib/getChildMoveData"
import { insertProjectChild } from "entities/project-children/lib/insertProjectChild"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"
import z from "zod"

import { evictFloor } from "../lib/evictFloor"
import { getFloorComplete } from "../lib/getFloorComplete"

const CreateFloorVariablesSchema = z.object({
  parentID: z.string(),
  parentType: z.string(),
  input: CreateFloorInputSchema()
})

export function useCreateFloor() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Floor.CREATE_FLOOR.Start"),
      success: t("Request.Floor.CREATE_FLOOR.Success"),
      failure: t("Request.Floor.CREATE_FLOOR.Failure")
    }
  })
  const { setElementPending, unsetElementPending } = useProjectStoreActions()
  const startProjectRequest = useProjectRequestMonitor()

  const cleanup = ({
    projectID,
    floorID
  }: {
    projectID: string
    floorID: string
  }) => {
    fetchProject({
      variables: { id: projectID },
      fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
    })
    unsetElementPending({ itemID: floorID, projectID })
  }

  const [apolloMutate, rest] = useApolloMutation<
    CreateFloorMutation,
    CreateFloorMutationVariables
  >(CreateFloorDocument, {
    update: (cache, result, options) => {
      if (result.data?.floor && options.variables?.parentID) {
        insertProjectChild({
          child: result.data?.floor,
          position: result.data?.floor.position,
          parentID: options.variables.parentID,
          parentType: mapAPITypeToTypename(
            options.variables.parentType
          ) as ProjectParentTypename,
          cache
        })
      }
    }
  })

  const createFloor = useEvent(
    async ({
      floor: floorData,
      displayPosition,
      projectID,
      parentID,
      parentType
    }: {
      floor: CreateFloorInput
      displayPosition?: number
      projectID: string
      parentID: string
      parentType: ProjectParentTypename
    }) => {
      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      await fetchProject({ variables: { id: projectID } })
      const id = floorData.id || generateUUID()
      const positionData = getChildPositionData({
        childID: id,
        childType: FloorTypename,
        projectID,
        displayPosition,
        parentID,
        cache: apolloCache
      })
      const floor = { ...floorData, position: positionData.nextPosition, id }
      const optimisticFloor = getFloorComplete(floor)

      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: CREATE_FLOOR
      })
      setElementPending({ itemID: id, projectID })
      emitAppEvent(CREATE_PROJECT_CHILD, {
        id,
        projectID,
        parentID,
        parentType,
        type: FloorTypename
      })

      return callMutation(apolloMutate, {
        variables: {
          parentID,
          parentType: mapTypenameToAPIType(parentType) as ProjectParentAPIType,
          input: floor
        },
        transform: data => data.floor as Floor,
        schemas: {
          input: CreateFloorVariablesSchema,
          response: MutationSchema.extend({
            floor: FloorSchema()
          })
        },
        apolloOptions: {
          optimisticResponse: optimisticFloor
            ? { __typename: MutationTypename, floor: optimisticFloor }
            : undefined,
          onCompleted: () => {
            completeProjectRequest()
            cleanup({ projectID, floorID: floor.id })
            raiseSuccessToast()
          },
          onError: () => {
            completeProjectRequest()
            evictFloor(floor.id, apolloCache)
            evictProjectPrice(projectID, apolloCache, true)
            cleanup({ projectID, floorID: floor.id })
            raiseErrorToast()
          }
        }
      })
    }
  )
  return [createFloor, rest] as [typeof createFloor, typeof rest]
}
